import { RotationError } from "../types/enums";
import {
  Avatar,
  GravitySample,
  KeyPoint,
  ReceivedKeypoint,
  RotationErrorDetails,
  Statistics,
} from "../types/interfaces";
import { GRAVITY_CONSTANT } from "./constants";
interface Keypoint {
  x: number;
  y: number;
  score: number;
}

export const convertKeypoints = (
  receivedKeypoints: ReceivedKeypoint[],
): KeyPoint[] => {
  return receivedKeypoints.map((keypoint) => ({
    bodyPart: keypoint.name,
    coordinate: { x: keypoint.x, y: keypoint.y },
  }));
};
export const median = (arr: number[]): number => {
  const sortedArray = arr.slice().sort((a, b) => a - b);
  const count = sortedArray.length;
  if (count % 2 === 0) {
    const middleIndex = count / 2;
    return (sortedArray[middleIndex - 1] + sortedArray[middleIndex]) / 2;
  } else {
    return sortedArray[Math.floor(count / 2)];
  }
};

export const base64ToBlob = (
  base64: string,
  mime: string = "image/png",
): Blob => {
  const byteString = atob(base64.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mime });
};

export function getGender(gender: string) {
  if (gender === "Male") {
    return gender;
  } else if (gender === "Female") {
    return gender;
  } else {
    return "Neutral";
  }
}

export function getDeviceInfo() {
  const userAgent = navigator.userAgent;
  const maxTouchPoints = navigator.maxTouchPoints;

  // General device identification
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isWindows = /Windows NT/i.test(userAgent);
  const isMac = /Macintosh/i.test(userAgent);

  // Default simplified device model from userAgent
  let deviceModel = "Unknown Device";

  // Detect iPhone and iPad more accurately
  if (isIOS) {
    if (
      /iPhone/i.test(userAgent) ||
      (isMac && maxTouchPoints > 1 && window.screen.width < 1024)
    ) {
      deviceModel = "iPhone";
    } else if (
      /iPad/i.test(userAgent) ||
      (isMac && maxTouchPoints > 1 && window.screen.width >= 1024)
    ) {
      deviceModel = "iPad";
    } else {
      deviceModel = "iPhone/iPad";
    }
  } else if (isAndroid) {
    deviceModel = "Android Device";
  } else if (isWindows) {
    deviceModel = "Windows PC";
  } else if (isMac) {
    deviceModel = "Mac";
  }

  return {
    model: deviceModel, // Accurate model info
  };
}
export function stringToBoolean(str: string) {
  return str.toLowerCase() === "true";
}
export function getBrowserVersion(): string {
  const userAgent = navigator.userAgent;
  let browserVersion = "Unknown Browser";

  if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
    const chromeVersion = userAgent.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = chromeVersion ? `Chrome ${chromeVersion[1]}` : "Chrome";
  } else if (userAgent.includes("Firefox")) {
    const firefoxVersion = userAgent.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = firefoxVersion
      ? `Firefox ${firefoxVersion[1]}`
      : "Firefox";
  } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    const safariVersion = userAgent.match(/Version\/(\d+\.\d+)/);
    browserVersion = safariVersion ? `Safari ${safariVersion[1]}` : "Safari";
  } else if (userAgent.includes("Edg")) {
    const edgeVersion = userAgent.match(/Edg\/(\d+\.\d+)/);
    browserVersion = edgeVersion ? `Edge ${edgeVersion[1]}` : "Edge";
  } else if (userAgent.includes("OPR") || userAgent.includes("Opera")) {
    const operaVersion = userAgent.match(/(OPR|Opera)\/(\d+\.\d+)/);
    browserVersion = operaVersion ? `Opera ${operaVersion[2]}` : "Opera";
  }

  return browserVersion;
}

export function getOSVersion(): string {
  const userAgent = navigator.userAgent;
  let osVersion = "Unknown OS";

  if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
    const iosVersion = userAgent.match(/OS (\d+[_\d]+)/);
    osVersion = iosVersion ? `iOS ${iosVersion[1].replace(/_/g, ".")}` : "iOS";
  } else if (userAgent.includes("Windows NT")) {
    const windowsVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = windowsVersion
      ? `Windows ${getWindowsName(windowsVersion[1])}`
      : "Windows";
  } else if (userAgent.includes("Mac OS X")) {
    const macVersion = userAgent.match(/Mac OS X (\d+[_\.\d]+)/);
    osVersion = macVersion
      ? `Mac OS X ${macVersion[1].replace(/_/g, ".")}`
      : "Mac OS";
  } else if (userAgent.includes("Android")) {
    const androidVersion = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = androidVersion ? `Android ${androidVersion[1]}` : "Android";
  }

  return osVersion;
}

function getWindowsName(version: string): string {
  switch (version) {
    case "10.0":
      return "10";
    case "6.3":
      return "8.1";
    case "6.2":
      return "8";
    case "6.1":
      return "7";
    case "6.0":
      return "Vista";
    case "5.1":
    case "5.2":
      return "XP";
    default:
      return version;
  }
}

export const getAvatarFromLocalStorage = (): Avatar | null => {
  const avatarData = localStorage.getItem("newAvatar");
  return avatarData ? (JSON.parse(avatarData) as Avatar) : null;
};
export function convertKgToLb(kg: number): number {
  return Number((kg * 2.20462).toFixed());
}
/** Inverse of convertKgToLb — used to seed the kg field from a lb value. */
export function convertLbToKg(lb: number): number {
  return Number((lb / 2.20462).toFixed());
}
export const convertCmToFeetInches = (cmValue: any) => {
  // Convert cm to inches
  const totalInches = parseFloat(cmValue) / 2.54; // Use parseFloat to retain decimal values
  const feet = Math.floor(totalInches / 12); // Calculate feet
  const inches = (totalInches % 12).toFixed(1); // Calculate remaining inches and round to 1 decimal place
  return `${feet}'${inches}''`; // Format as feet and inches
};
export function convertToInches(height: any) {
  // Extract feet and inches from the height string
  const [feet, inches] = height.split("'").map((value: any) => {
    if (typeof value !== "string") return Number(value);
    // make sure both strings can be converted to numbers
    // by removing any non-numeric characters
    return Number(value.replace(/[^\d.]/g, ""));
  });
  const totalInches = feet * 12 + inches; // Convert feet to inches and add the inches
  return totalInches; // Return the total inches
}
export const getJointCoordinates = (keypoint: Keypoint): number[] => {
  return [keypoint.x, keypoint.y];
};
export const handleRotationError = (
  error: RotationError,
): RotationErrorDetails => {
  let errorMsg: string;
  let errorDesc: string;

  switch (error) {
    case "overRotation":
      errorMsg = "Over rotation";
      errorDesc =
        "Complete one full spin. Stop and stay stationary once you are facing the camera again.";
      break;
    case "underRotation":
      errorMsg = "Incomplete rotation";
      errorDesc = "Complete one full spin until you face the camera again.";
      break;
    case "rotatedTooQuickly":
      errorMsg = "Rotated too quickly";
      errorDesc = "Complete one full spin during the scan countdown.";
      break;
    case "bodyOutOfView":
      errorMsg = "Did not rotate in place";
      errorDesc =
        "Stay in place while rotating. Do not move closer or further from the camera. Try taking smaller steps.";
      break;
    case "deviceMoved":
      errorMsg = "Device moved";
      errorDesc = "Keep your device stationary during the scan capture.";
      break;

    case "bodyNotDetected":
      errorMsg = "Body not detected";
      errorDesc =
        "Make sure the top of your head and bottom of your feet are fully in view of the camera. Stand up straight.";
      break;
    case "insufficientRotationData":
      errorMsg = "Incomplete data capture";
      errorDesc = "Uh oh! Something went wrong. Please try again.";
      break;
    default:
      errorMsg = "Unknown error";
      errorDesc = "An unknown error has occurred. Please try again.";
  }

  return { errorMsg, errorDesc };
};
export const getHTMLString = (
  subject: string,
  msg: string,
  emailBy: string,
  userID: string,
  appVersion: string,
): string => {
  const htmlString = `
        <h1>${subject}</h1>
        <p>${msg}</p>
        <footer>Message sent by: ${emailBy ? emailBy : userID}</footer>
        <hr/>
        <p><strong>User ID:</strong> ${userID}</p>
        <p><strong>PWA Version:</strong> ${appVersion}</p>
    `;
  return htmlString;
};

export const convertToG = (sample: GravitySample): GravitySample => ({
  x: sample.x / GRAVITY_CONSTANT,
  y: sample.y / GRAVITY_CONSTANT,
  z: sample.z / GRAVITY_CONSTANT,
});

export const computeStatistics = (buffer: GravitySample[]): Statistics => {
  const length = buffer.length;

  const sum = buffer.reduce(
    (acc, sample) => ({
      x: acc.x + sample.x,
      y: acc.y + sample.y,
      z: acc.z + sample.z,
    }),
    { x: 0, y: 0, z: 0 },
  );

  const mean = {
    x: sum.x / length,
    y: sum.y / length,
    z: sum.z / length,
  };

  const varianceSum = buffer.reduce(
    (acc, sample) => ({
      x: acc.x + Math.pow(sample.x - mean.x, 2),
      y: acc.y + Math.pow(sample.y - mean.y, 2),
      z: acc.z + Math.pow(sample.z - mean.z, 2),
    }),
    { x: 0, y: 0, z: 0 },
  );

  const variance = {
    x: varianceSum.x / length,
    y: varianceSum.y / length,
    z: varianceSum.z / length,
  };

  return { mean, variance };
};

export const isStationary = (variance: GravitySample): boolean => {
  const varianceThreshold = Math.pow(10e-3, 2);
  return (
    variance.x < varianceThreshold &&
    variance.y < varianceThreshold &&
    variance.z < varianceThreshold
  );
};

export const normalizeGravity = (mean: GravitySample): GravitySample => {
  const magnitude = Math.sqrt(
    Math.pow(mean.x, 2) + Math.pow(mean.y, 2) + Math.pow(mean.z, 2),
  );
  return {
    x: mean.x / magnitude,
    y: mean.y / magnitude,
    z: mean.z / magnitude,
  };
};
export const multiplyMatrixAndVector = (
  matrix: number[][],
  vector: number[],
): number[] => {
  return [
    matrix[0][0] * vector[0] +
      matrix[0][1] * vector[1] +
      matrix[0][2] * vector[2],
    matrix[1][0] * vector[0] +
      matrix[1][1] * vector[1] +
      matrix[1][2] * vector[2],
    matrix[2][0] * vector[0] +
      matrix[2][1] * vector[1] +
      matrix[2][2] * vector[2],
  ];
};
